import React, { useState } from 'react';
import Input from '../../../../components/Input/Input';
import { messages } from './Messages';
import Select from '../../../../components/Select';
import { IOption } from '../../../../components/Select/Select';
import HoverIcon from '../../../../components/HoverIcon/HoverIcon';
import cloneDeep from 'lodash/cloneDeep';
import { MyCell } from './style';
import Button from '../../../../components/Button/Button';
import { PDPClass, Topic, TopicStatus } from 'store/pdp/types';
import { ITags } from 'store/providers/providerTypes';
import Toggle from '../../../../components/Toggle';
import { Card } from '../CompanyRoles/styles';
import { lightGray, exactWhite, gray } from '../../../../utils/style/colors';
import { ISurveyItem } from '../../../../store/survey/surveyTypes';
import { createOrUpdateTopicFn } from '../../../../store/pdp/pdpSlice';

interface ComponentProps {
  topic?: Topic;
  forApprove?: boolean;
  new?: boolean;
  allRoleOptions: IOption[];
  selectTagOptions: { [key: number]: IOption[] };
  createOrUpdateTopic: (
    topic: Topic
  ) => ReturnType<typeof createOrUpdateTopicFn>;
  allTags: ITags['data'];
  pdpId: number;
  classTemplate?: PDPClass;
  editPDPClass: () => void;
  handleOpenItem?: (itemId: number) => void;
  handleOpenDirectItem?: (item: ISurveyItem) => void;
  loadingItem?: number;
  createPDPClass: (topicId: number) => Promise<any>;
  edit?: boolean;
  isSideBarCollapsed?: boolean;
  availableTopicList?: { title: string; id: number }[];
}

export const TopicEditor: React.FC<ComponentProps> = (props) => {
  const [editing, setEditing] = useState(props.new || !!props.edit);
  const [batchAdd, setBatchAdd] = useState(false);
  const [batchAddTopicNames, setBatchAddTopicNames] = useState([] as string[]);
  const newTopicSample = {
    properties: {
      relatedToRoles: [],
      tags: [],
      name: '',
      mandatoryForRoles: [],
    },
    companyPDPId: props.pdpId,
    status: 'Exists',
    questionBankSurvey: undefined,
  } as Topic;
  const [editingTopic, setEditingTopic] = useState(
    props.new
      ? newTopicSample
      : props.edit
        ? props.topic
        : (undefined as Topic | undefined)
  );
  const topic = editing ? editingTopic! : props.topic!;
  const [loading, setLoading] = useState(false);
  const save = () => {
    if (!batchAdd ? !topic.properties.name : batchAddTopicNames.length === 0) {
      alert(messages.nameCantBeEmpty);
      return;
    }
    setLoading(true);

    function resetInputs() {
      if (props.new) {
        editingTopic!.properties.name = '';
        editingTopic!.properties.relatedToRoles = [];
        editingTopic!.properties.mandatoryForRoles = [];
        editingTopic!.properties.tags = [];
        setEditingTopic({ ...editingTopic! });
      }
    }

    if (batchAdd) {
      const plist = batchAddTopicNames.map((name) => {
        editingTopic!.properties.name = name!;
        return props.createOrUpdateTopic(editingTopic!);
      });
      Promise.all(plist).finally(() => {
        setEditing(props.new || false);
        setLoading(false);
        resetInputs();
      });
    } else {
      props.createOrUpdateTopic(editingTopic!).then(() => {
        setEditing(props.new || false);
        setLoading(false);
        resetInputs();
      });
    }
  };
  const isDuplicatedTitle = batchAdd
    ? props.availableTopicList?.some((it) =>
        batchAddTopicNames.includes(it.title)
      )
    : editingTopic?.properties.name && props.availableTopicList
      ? props.availableTopicList.some(
          (it) =>
            it.title === editingTopic?.properties.name &&
            it.id !== editingTopic.id
        )
      : false;

  const element = (
    <>
      <>
        <tr
          style={{
            position: 'relative',
            display: editing ? 'flex' : undefined,
            flexWrap: 'wrap',
            alignItems: props.new ? 'flex-start' : 'flex-end',
            flexDirection: props.new ? 'column' : 'row',
            borderBottom: !editing ? `1px solid ${lightGray}` : undefined,
            borderTop: !editing ? `1px solid ${lightGray}` : undefined,
          }}
        >
          <MyCell
            style={{
              minWidth: '150px',
              position: 'sticky',
              right: 0,
              zIndex: 2,
              backgroundColor: exactWhite,
            }}
          >
            {editing ? (
              <>
                {props.new && (
                  <Toggle
                    wrapperStyle={{}}
                    label={messages.batchAdd}
                    name="batchAdd"
                    title={messages.batchAdd}
                    onChange={(e: any) => setBatchAdd(e.target.checked)}
                    checked={batchAdd}
                  />
                )}
                {batchAdd ? (
                  <Select
                    values={batchAddTopicNames.map((it) => ({
                      value: it,
                      label: it,
                    }))}
                    options={[]}
                    letAddChip={true}
                    placeholder={messages.topicNamesPlaceHolder}
                    multiChoice={true}
                    label={messages.topicNamesPlaceHolder}
                    onChangeValues={(it) => {
                      setBatchAddTopicNames(it.map((it) => it.label));
                    }}
                  />
                ) : (
                  <div>
                    <Input
                      id={'title' + (topic?.id || 'new')}
                      type="text"
                      value={topic?.properties.name}
                      onChange={(event) => {
                        const update = cloneDeep(topic);
                        update!.properties.name = event.target.value;
                        setEditingTopic({ ...update! });
                      }}
                      label={editing ? messages.title : undefined}
                    />
                  </div>
                )}
                {isDuplicatedTitle && (
                  <div className="text-xs text-red-500">
                    {messages.duplicatedName}
                  </div>
                )}
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{topic?.properties.name}</span>
                {topic?.properties.offeredBy === 'User' && (
                  <span style={{ color: gray, fontSize: '.8rem' }}>
                    {messages.suggestedTopic}{' '}
                  </span>
                )}
              </div>
            )}
          </MyCell>
          <MyCell>
            <Select
              disabled={!editing}
              label={editing ? messages.optionalRoles : undefined}
              values={props.allRoleOptions.filter(
                (it) =>
                  topic?.properties.relatedToRoles.includes(Number(it.value)) &&
                  !(topic?.properties.mandatoryForRoles || []).includes(
                    Number(it.value)
                  )
              )}
              options={props.allRoleOptions.map((it) => {
                if (
                  topic?.properties.mandatoryForRoles?.includes(
                    Number(it.value)
                  )
                )
                  return { ...it, disabled: true };
                return it;
              })}
              multiChoice={true}
              onChangeValues={(selected) => {
                const update = cloneDeep(topic);
                update!.properties!.relatedToRoles = [
                  ...selected.map((it) => Number(it.value)),
                  ...(update!.properties.mandatoryForRoles || []),
                ];
                setEditingTopic(update);
              }}
              placeholder={messages.noOne}
              showPlaceHolderWhenDisabled={true}
            />
          </MyCell>
          <MyCell>
            <Select
              disabled={!editing}
              label={editing ? messages.mandatoryRoles : undefined}
              values={props.allRoleOptions.filter(
                (it) =>
                  topic?.properties.relatedToRoles.includes(Number(it.value)) &&
                  topic?.properties.mandatoryForRoles?.includes(
                    Number(it.value)
                  )
              )}
              options={props.allRoleOptions}
              multiChoice={true}
              onChangeValues={(selected) => {
                const update = cloneDeep(topic);
                const mandatoryRoles = selected.map((it) => Number(it.value));
                update!.properties!.relatedToRoles = [
                  ...mandatoryRoles,
                  ...update!.properties!.relatedToRoles.filter(
                    (it) =>
                      topic?.properties.relatedToRoles.includes(Number(it)) &&
                      !topic?.properties.mandatoryForRoles?.includes(it)
                  ),
                ];
                update!.properties!.mandatoryForRoles = mandatoryRoles;
                setEditingTopic(update);
              }}
              placeholder={messages.noOne}
              showPlaceHolderWhenDisabled={true}
            />
          </MyCell>
          {Object.entries(props.selectTagOptions).map(([key, options]) => {
            return (
              <MyCell key={key}>
                <Select
                  disabled={!editing}
                  onChangeValues={(values) => {
                    const optionIds = options.map((it) => Number(it.value));
                    const newTags: number[] =
                      topic?.properties?.tags.filter(
                        (it) => !optionIds.includes(it)
                      ) || [];
                    values.forEach((it) => {
                      newTags.push(Number(it.value));
                    });
                    const newTopic = cloneDeep(topic);
                    newTopic.properties.tags = newTags;
                    setEditingTopic({ ...newTopic! });
                  }}
                  options={options}
                  values={options.filter((it) =>
                    topic?.properties.tags?.includes(Number(it.value))
                  )}
                  multiChoice={true}
                  label={editing ? props.allTags[Number(key)]?.text : undefined}
                />
              </MyCell>
            );
          })}
          {!props.new && (
            <MyCell>
              <Select
                allowRemove={false}
                disabled={!editing}
                label={editing ? messages.status : undefined}
                onChangeValues={(values) => {
                  const update = cloneDeep(topic);
                  update!.status = (values[0]?.value ||
                    'Exists') as TopicStatus;
                  if (
                    update!.status !== 'Proposed' &&
                    update.properties.name.endsWith('*')
                  )
                    update.properties.name = update.properties.name.substring(
                      0,
                      update.properties.name.length - 1
                    );

                  setEditingTopic(update);
                }}
                options={(topic.status === 'Proposed' || props.forApprove
                  ? ['Exists', 'Proposed', 'Archived']
                  : ['Exists', 'Archived', 'Disabled']
                ).map((it) => ({
                  value: it,
                  label: messages['State_' + it],
                }))}
                values={[
                  { value: topic.status, label: messages[topic.status] },
                ]}
              />
            </MyCell>
          )}
          {
            <MyCell
              style={{
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: exactWhite,
              }}
            >
              {!props.new && !editing && (
                <>
                  <HoverIcon
                    onClick={() => {
                      setEditing(true);
                      setEditingTopic(props.topic);
                    }}
                    type="edit"
                    color={'blue'}
                  />
                  {props.handleOpenDirectItem && topic.questionBankSurvey && (
                    <HoverIcon
                      onClick={() => {
                        props.handleOpenDirectItem!(topic.questionBankSurvey!);
                      }}
                      type="list"
                      color={'green'}
                      loading={
                        props.loadingItem === topic.questionBankSurvey.id
                      }
                      title={messages.questionBank}
                    />
                  )}
                </>
              )}
              {editing && (
                <>
                  <Button
                    onClick={save}
                    style={{ margin: '10px' }}
                    loading={loading}
                    color={'blue'}
                    disabled={
                      isDuplicatedTitle
                        ? true
                        : !batchAdd
                          ? !topic.properties.name
                          : batchAddTopicNames.length === 0
                    }
                    aria-disabled={!topic.properties.name}
                  >
                    {messages.save}
                  </Button>
                  {!props.new && !props.forApprove && (
                    <Button
                      onClick={() => {
                        setEditing(false);
                      }}
                      style={{ margin: '10px' }}
                      color={'red-outline'}
                      disabled={
                        loading || !batchAdd
                          ? !topic.properties.name
                          : batchAddTopicNames.length === 0
                      }
                      aria-disabled={!topic.properties.name}
                    >
                      {messages.cancel}
                    </Button>
                  )}
                </>
              )}
            </MyCell>
          }
          {/*{!props.new && (
          <td>
            {props.classTemplate ? (
              <Button
                onClick={() => {
                  props.editPDPClass();
                }}
              >
                <Icon type={'edit'} />
                {messages.editClassTemplate}
              </Button>
            ) : (
              <Button
                loading={creatingClass}
                onClick={() => {
                  setCreatingClass(true);
                  props.createPDPClass(topic.id!).then(() => {
                    setCreatingClass(false);
                    // props.editPDPClass();
                  });
                }}
                color="red-outline"
              >
                <Icon type={'plus'} />
                {messages.createClassTemplate}
              </Button>
            )}
          </td>
        )}*/}
        </tr>
        {/*{editing && (
        <tr>
          <MyCell style={{ padding: '0px 5px' }} colSpan={5}>
            {messages.emptyNotic}{' '}
          </MyCell>
        </tr>
      )}*/}
      </>
    </>
  );
  if (!editing) return element;
  return (
    <tr>
      <td colSpan={'100%' as any}>
        {props.new ? (
          element
        ) : (
          <Card
            style={{
              width: ` calc(min(100% - 20px , 100vw - 90px - ${props.isSideBarCollapsed ? '60px' : '235px'}))`,
              position: 'sticky',
              right: '1rem',
              zIndex: 3,
            }}
          >
            {element}
          </Card>
        )}
      </td>
    </tr>
  );
};
